import React, { useState, useRef } from 'react';

const MODE_DEFINITIONS = [
    {
        id: 'style',
        name: 'Transfert de Style',
        tokenCost: 3,
        icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 19l7-7 3 3-7 7-3-3z"/><path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"/><path d="M2 2l7.586 7.586"/><circle cx="11" cy="11" r="2"/></svg>,
        description: 'L\'IA absorbe l\'ambiance, les couleurs et les traits de pinceau de l\'image (Redux).'
    },
    {
        id: 'mascot',
        name: 'Cohérence Mascotte',
        tokenCost: 4,
        icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M2 10a4 4 0 0 1 4-4h12a4 4 0 0 1 4 4v5a7 7 0 0 1-14 0v-5z"/><path d="M8 14v1"/><path d="M16 14v1"/><path d="M12 16v1"/></svg>,
        description: 'Vise à cloner et conserver la cohérence du personnage principal (IP-Adapter Face).'
    },
    {
        id: 'structure',
        name: 'Reprise de Structure',
        tokenCost: 5,
        icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21.3 15.3l-7.6-7.6a2 2 0 0 0-2.8 0l-7.6 7.6a2 2 0 0 0 0 2.8l7.6 7.6a2 2 0 0 0 2.8 0l7.6-7.6a2 2 0 0 0 0-2.8z"/><path d="M14.5 9.5L9.5 14.5"/></svg>,
        description: 'Force l\'IA à respecter scrupuleusement l\'architecture et les contours stricts (ControlNet).'
    }
];

// La borne annoncée sous la zone de dépôt (« Max 20MB ») n'était appliquée
// nulle part : un fichier de 80 Mo, ou un document qui n'était pas une image,
// était accepté sans un mot — la lecture partait quand même et l'utilisateur
// n'avait aucune raison de recommencer avec un autre fichier.
const MAX_REFERENCE_IMAGE_BYTES = 20 * 1024 * 1024;

const referenceImageRefusal = (file) => {
    if (!file.type || !file.type.startsWith('image/')) {
        return "Ce fichier n'est pas une image : importez un JPG, un PNG ou un WEBP.";
    }
    if (file.size > MAX_REFERENCE_IMAGE_BYTES) {
        const poids = (file.size / (1024 * 1024)).toFixed(1).replace('.', ',');
        return `Cette image pèse ${poids} Mo, au-delà de la limite de 20 Mo : réduisez sa résolution, ou choisissez un fichier plus léger.`;
    }
    return null;
};

const TABS = [
    { id: 'inspiration', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>, label: 'Inspiration' },
    { id: 'vectorisation', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 19l7-7 3 3-7 7-3-3z"/><path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"/></svg>, label: 'Vectorisation IA' },
    { id: 'detourage', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="6" cy="6" r="3"/><circle cx="6" cy="18" r="3"/><line x1="20" y1="4" x2="8.12" y2="15.88"/><line x1="14.47" y1="14.48" x2="20" y2="20"/><line x1="8.12" y1="8.12" x2="12" y2="12"/></svg>, label: 'Détourage Smart' },
    { id: 'krea', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>, label: 'Génération Live' },
    { id: 'outpainting', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>, label: 'Outpainting' },
    { id: 'galerie', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20"/></svg>, label: 'Galerie Projets' }
];

export default function InspirationStudioModal({ isOpen, onClose }) {
    const [activeTab, setActiveTab] = useState('inspiration');
    const [selectedMode, setSelectedMode] = useState('style');
    const [referenceImage, setReferenceImage] = useState(null);
    const [inspirationStrength, setInspirationStrength] = useState(0.8);
    const [textPrompt, setTextPrompt] = useState("");
    const [isGenerating, setIsGenerating] = useState(false);
    
    // Simulation du porte-monnaie
    const [userTokens, setUserTokens] = useState(120); 
    
    const fileInputRef = useRef(null);

    const activeModeDef = MODE_DEFINITIONS.find(m => m.id === selectedMode);

    if (!isOpen) return null;

    // Les deux chemins d'import (bouton et glisser-déposer) passent désormais par
    // le même contrôle : avant, le dépôt d'un fichier non conforme ne provoquait
    // rien du tout à l'écran, et l'utilisateur croyait son image chargée.
    const loadReferenceImage = (file) => {
        if (!file) return;
        const refus = referenceImageRefusal(file);
        if (refus) {
            alert(refus);
            return;
        }
        const reader = new FileReader();
        reader.onload = (event) => setReferenceImage(event.target.result);
        reader.onerror = () => alert("Cette image n'a pas pu être lue : vérifiez qu'elle n'est pas endommagée, puis réessayez.");
        reader.readAsDataURL(file);
    };

    const handleImageUpload = (e) => {
        loadReferenceImage(e.target.files[0]);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        loadReferenceImage(e.dataTransfer.files[0]);
    };

    const handleGenerate = () => {
        // Il y avait ici une alerte « Veuillez importer une image de référence
        // d'abord. » que personne n'a jamais pu voir : le bouton de lancement est
        // `disabled` dès que `referenceImage` est vide, et il est le seul appelant
        // de cette fonction. Le refus est donc porté par l'état désactivé du
        // bouton, pas par un message. On garde une sortie muette comme garde-fou
        // au cas où un futur appelant contournerait le bouton.
        if (!referenceImage) return;
        if (userTokens < activeModeDef.tokenCost) {
            alert("Crédits insuffisants. Veuillez recharger votre portefeuille.");
            return;
        }

        setIsGenerating(true);
        // Simulation de la requête
        setTimeout(() => {
            setIsGenerating(false);
            setUserTokens(prev => prev - activeModeDef.tokenCost);
            alert(`Simulation : Génération ${activeModeDef.name} terminée (-${activeModeDef.tokenCost} tokens)`);
        }, 3000);
    };

    return (
        <div className="fixed inset-0 z-[140] flex items-center justify-center modal-backdrop-strong backdrop-blur-sm p-4 font-mono text-sm shadow-[inset_0_0_100px_rgba(0,0,0,0.5)] transition-all">
            <div className="bg-black/80 backdrop-blur-md border border-white/10 w-full max-w-[90vw] rounded-3xl shadow-[0_0_50px_rgba(0,0,0,0.5)] flex overflow-hidden h-[85vh] min-h-[500px] max-h-[700px] relative ring-1 ring-white/5">
                
                {/* BOUTON FERMER GLOBAL */}
                <button onClick={onClose} disabled={isGenerating} className="absolute top-4 right-4 z-50 bg-black/80 hover:bg-red-900/80 border border-white/10 text-white w-8 h-8 rounded-full flex items-center justify-center transition-all disabled:opacity-50 shadow-[0_0_15px_rgba(0,0,0,0.5)] backdrop-blur-md">✕</button>

                {/* BARRE LATÉRALE : Menus I.A */}
                <div className="w-64 bg-black/80 border-r border-white/5 flex flex-col shrink-0 z-10 shadow-2xl backdrop-blur-md">
                    <div className="p-5 border-b border-white/5 bg-gradient-to-b from-white/5 to-transparent">
                        <h2 className="text-white font-bold uppercase tracking-widest text-xs flex items-center gap-2 drop-shadow-[0_0_15px_rgba(255,255,255,0.5)]">
                            <span className="text-white">✧</span> STUDIO I.A
                        </h2>
                    </div>
                    
                    <div className="flex-1 p-3 flex flex-col gap-1.5 overflow-y-auto custom-scrollbar">
                        {TABS.map(tab => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-[10px] font-bold transition-all duration-300 ${
                                    activeTab === tab.id 
                                    ? 'bg-white/10 border border-white/20 text-white shadow-[0_0_20px_rgba(255,255,255,0.05),inset_0_0_10px_rgba(255,255,255,0.05)] translate-x-1' 
                                    : 'text-white/40 hover:bg-white/5 border border-transparent hover:text-white hover:pl-4'
                                }`}
                            >
                                <span className={`flex justify-center items-center shrink-0 w-4 h-4 transition-all duration-500 ${activeTab === tab.id ? 'drop-shadow-[0_0_10px_rgba(255,255,255,0.8)]' : 'grayscale opacity-50'}`}>{tab.icon}</span>
                                <span className="uppercase tracking-widest">{tab.label}</span>
                            </button>
                        ))}
                    </div>

                    {/* Footer Menu : Portefeuille */}
                    <div className="p-4 border-t border-white/5 bg-black/60">
                        <div className="flex items-center justify-between bg-black/80 border border-white/10 py-2.5 px-3 rounded-xl text-[10px] shadow-inner backdrop-blur-lg group hover:border-white/20 transition-all">
                            <span className="text-white/40 font-bold uppercase tracking-widest text-[8px]">Solde I.A</span>
                            <span className="text-yellow-500 font-bold tracking-widest drop-shadow-[0_0_8px_rgba(255,255,255,0.5)]">✧ {userTokens}</span>
                        </div>
                    </div>
                </div>

                {/* ZONE DE CONTENU PRINCIPALE */}
                <div className="flex-1 flex bg-transparent relative overflow-hidden">
                    
                    {activeTab === 'inspiration' ? (
                        /* ----- MODULE 1 : INSPIRATION (FLUX + REDUX) ----- */
                        <>
                            {/* Colonne Centre : Drag & Drop de l'Image Réf */}
                            <div 
                                className="w-1/2 p-5 border-r border-white/5 flex flex-col relative group bg-gradient-to-br from-white/5 to-transparent"
                                onDragOver={e => e.preventDefault()}
                                onDrop={handleDrop}
                            >
                                <div className="absolute top-4 left-4 z-10 flex items-center gap-2">
                                    <span className="bg-black/80 backdrop-blur-md border border-white/10 text-white/70 px-3 py-1 rounded-full text-[8px] font-bold shadow-sm uppercase tracking-widest">Source Visuelle</span>
                                </div>

                                <div className="flex-1 border border-dashed border-white/20 hover:border-white/50 rounded-2xl flex flex-col items-center justify-center relative overflow-hidden transition-all bg-gradient-to-b from-white/5 to-transparent shadow-[inset_0_0_50px_rgba(0,0,0,0.5)] hover:shadow-[inset_0_0_50px_rgba(255,255,255,0.05)] mt-10">
                                    {referenceImage ? (
                                        <>
                                            <img src={referenceImage} alt="Reference" className="w-full h-full object-contain p-4 drop-shadow-2xl" />
                                            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
                                            <button 
                                                onClick={() => setReferenceImage(null)}
                                                className="absolute bottom-8 bg-red-900/80 hover:bg-red-800 text-white w-12 h-12 rounded-full flex items-center justify-center transition-all opacity-0 group-hover:opacity-100 shadow-[0_0_20px_rgba(255,0,0,0.3)] backdrop-blur-md border border-white/20 z-20 hover:scale-110"
                                                title="Supprimer l'image" aria-label="Supprimer l'image"
                                            >
                                                ✕
                                            </button>
                                        </>
                                    ) : (
                                        <div className="text-center px-8 group/upload cursor-pointer" onClick={() => fileInputRef.current?.click()}>
                                            <svg className="w-16 h-16 mx-auto mb-6 opacity-30 group-hover/upload:opacity-70 group-hover/upload:text-white transition-all duration-500 group-hover/upload:scale-110 drop-shadow-[0_0_15px_rgba(255,255,255,0.3)]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                                            <p className="text-white/80 font-bold mb-3 uppercase tracking-[0.2em] text-xs">Glissez votre Référence</p>
                                            <p className="text-[10px] text-white/30 mb-8 font-mono tracking-widest">JPG, PNG, WEBP (Max 20MB)</p>
                                            <button 
                                                className="px-8 py-3 bg-white/5 group-hover/upload:bg-white/10 border border-white/10 rounded-full text-white text-[10px] uppercase font-bold tracking-[0.15em] transition-all shadow-[0_0_15px_rgba(255,255,255,0.02)] group-hover/upload:shadow-[0_0_20px_rgba(255,255,255,0.1)] backdrop-blur-md pointer-events-none"
                                            >
                                                Explorer les fichiers
                                            </button>
                                        </div>
                                    )}
                                    <input type="file" ref={fileInputRef} onChange={handleImageUpload} accept="image/*" className="hidden" />
                                </div>
                            </div>

                            {/* Colonne Droite : Paramétrage IA */}
                            <div className="w-1/2 flex flex-col bg-transparent shadow-[inset_-10px_0_20px_rgba(0,0,0,0.5)]">
                                <div className="flex-1 overflow-y-auto p-6 space-y-6 custom-scrollbar">
                                    
                                    {/* Section 1 : Piliers Architecturaux */}
                                    <div>
                                        <h3 className="block text-[8px] text-white/40 font-mono font-bold uppercase mb-3 tracking-[0.2em]">1. Ingénierie (Mode I.A)</h3>
                                        <div className="grid grid-cols-1 gap-3">
                                            {MODE_DEFINITIONS.map(mode => (
                                                <div 
                                                    key={mode.id}
                                                    onClick={() => !isGenerating && setSelectedMode(mode.id)}
                                                    className={`p-3 rounded-xl border cursor-pointer transition-all duration-300 flex items-start gap-3 ${
                                                        selectedMode === mode.id 
                                                        ? 'bg-white/10 border-white/20 shadow-[0_0_30px_rgba(255,255,255,0.05),inset_0_0_15px_rgba(255,255,255,0.05)] translate-x-1' 
                                                        : 'bg-black/40 border-white/5 hover:bg-white/5 hover:border-white/10'
                                                    } ${isGenerating ? 'opacity-30 cursor-not-allowed' : ''}`}
                                                >
                                                    <div className={`flex justify-center items-center w-6 h-6 mt-1 transition-all ${selectedMode === mode.id ? 'drop-shadow-[0_0_10px_rgba(255,255,255,0.8)] filter brightness-125' : 'opacity-30 grayscale'}`}>
                                                        {mode.icon}
                                                    </div>
                                                    <div className="flex-1">
                                                        <div className="flex items-center justify-between mb-1.5">
                                                            <h4 className={`font-bold uppercase tracking-[0.1em] text-[10px] transition-colors ${selectedMode === mode.id ? 'text-white drop-shadow-md' : 'text-white/60'}`}>
                                                                {mode.name}
                                                            </h4>
                                                            <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded-md tracking-widest ${selectedMode === mode.id ? 'bg-white text-black shadow-[0_0_10px_rgba(255,255,255,0.5)]' : 'bg-black/50 text-white/50 border border-white/10'}`}>
                                                                {mode.tokenCost} ✧
                                                            </span>
                                                        </div>
                                                        <p className="text-[9px] text-white/40 leading-relaxed font-mono">{mode.description}</p>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Section 2 : Prompt de Fusion */}
                                    <div>
                                        <div className="mb-3">
                                            <h3 className="block text-[8px] text-white/40 font-mono font-bold uppercase tracking-[0.2em]">2. Orientation Lexicale</h3>
                                        </div>
                                        <textarea 
                                            value={textPrompt}
                                            onChange={e => setTextPrompt(e.target.value)}
                                            disabled={isGenerating}
                                            placeholder="Paramètres additionnels (ex: 'néon, pluie, cyberpunk')..."
                                            className="w-full bg-black/40 border border-white/5 shadow-inner rounded-xl p-3 text-white hover:border-white/10 focus:border-white/20 outline-none text-[10px] h-20 custom-scrollbar resize-none font-mono placeholder:text-white/20 transition-all"
                                        />
                                    </div>

                                    {/* Section 3 : Curseurs */}
                                    <div>
                                        <div className="mb-3">
                                            <h3 className="block text-[8px] text-white/40 font-mono font-bold uppercase tracking-[0.2em]">3. Puissance Référentielle</h3>
                                        </div>
                                        <div className="bg-black/40 px-5 py-4 rounded-xl border border-white/5 shadow-inner">
                                            <div className="flex justify-between items-center mb-4">
                                                <label className="text-[9px] text-white/50 font-bold uppercase tracking-widest font-mono">Cohésion Strict</label>
                                                <span className="text-[10px] font-mono font-bold text-white bg-white/10 px-2.5 py-1 rounded-md border border-white/5 shadow-inner backdrop-blur-md">
                                                    {Math.round(inspirationStrength * 100)}%
                                                </span>
                                            </div>
                                            <input 
                                                type="range" 
                                                min="0.1" max="1.0" step="0.05" 
                                                value={inspirationStrength} 
                                                onChange={e => setInspirationStrength(Number(e.target.value))}
                                                disabled={isGenerating}
                                                className="w-full"
                                            />
                                            <div className="flex justify-between text-[7px] text-white/30 mt-3 uppercase font-bold tracking-[0.2em] font-mono">
                                                <span>Subtile (10%)</span>
                                                <span>Mimétisme Absolu (100%)</span>
                                            </div>
                                        </div>
                                    </div>

                                </div>

                                {/* Lancement de la tâche */}
                                <div className="p-6 border-t border-white/5 bg-black/60 flex items-center justify-end shrink-0 backdrop-blur-xl">
                                    <button 
                                        onClick={handleGenerate} 
                                        disabled={isGenerating || !referenceImage} 
                                        className={`px-6 py-3 rounded-xl transition-all duration-300 uppercase tracking-[0.15em] font-bold text-[10px] flex items-center gap-3 border overflow-hidden relative group/btn ${
                                            isGenerating || !referenceImage 
                                            ? 'bg-black/80 border-white/5 text-white/30 opacity-60 cursor-not-allowed' 
                                            : 'bg-white text-black border-white shadow-[0_0_30px_rgba(255,255,255,0.3)] hover:scale-[1.02] hover:shadow-[0_0_50px_rgba(255,255,255,0.5)]'
                                        }`}
                                    >
                                        {!isGenerating && referenceImage && (
                                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/50 to-transparent -translate-x-full group-hover/btn:translate-x-full duration-1000 transition-transform"></div>
                                        )}
                                        {isGenerating ? (
                                            <><span className="animate-spin text-sm block leading-none">↻</span> Mixage I.A...</>
                                        ) : (
                                            <>
                                                Fusion Absolue
                                                <div className="h-4 w-px bg-black/20 mx-1"></div>
                                                <span className="bg-black/10 px-2 py-0.5 rounded shadow-inner border border-black/5 text-[9px] font-mono font-bold leading-none">-{activeModeDef.tokenCost} ✧</span>
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </>
                    ) : (
                        /* ----- AUTRES MODULES EN CONSTRUCTION ----- */
                        <div className="w-full h-full flex flex-col items-center justify-center p-8 text-center bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMSIgY3k9IjEiIHI9IjEiIGZpbGw9InJnYmEoMjU1LDI1NSwyNTUsMC4wNSkiLz48L3N2Zz4=')]">
                            <span className="text-5xl opacity-80 mb-6 drop-shadow-[0_0_15px_rgba(255,255,255,0.2)]">
                                {TABS.find(t => t.id === activeTab)?.icon}
                            </span>
                            <h3 className="text-xl font-bold uppercase tracking-widest text-white mb-2">
                                Module {TABS.find(t => t.id === activeTab)?.label}
                            </h3>
                            <p className="text-white/40 text-xs max-w-sm mb-6 leading-relaxed">
                                Le maillage API pour cet utilitaire est en attente de synchronisation AWS S3 / RunPod. Le moteur neuronal sera activé prochainement.
                            </p>
                            <div className="inline-block px-4 py-1.5 border border-forge-border/60 bg-black/50 rounded text-[10px] text-yellow-500 font-bold uppercase tracking-wider">
                                Statut : Développement Core I.A
                            </div>
                        </div>
                    )}

                </div>
            </div>
        </div>
    );
}
